<template>
<v-dialog
  v-model="value"
  max-width="680px"
  persistent
  scrollable
  content-class="preset-dialog"
>
  <v-card class="rounded-lg" style="border-radius: 12px !important; overflow: hidden;">
    <div v-if="loading" class="loader-overlay">
      <div class="loader-content">
        <v-progress-circular
          indeterminate
          size="48"
          width="4"
          color="#009E60"
        />
        <div class="loader-text mt-3">Сохранение пресета...</div>
      </div>
    </div>

    <!-- Header -->
    <v-card-title class="px-6 pt-5 pb-4 d-flex justify-space-between align-center" style="border-bottom: 1px solid #DEE1EA;">
      <span class="text-h6 font-weight-bold" style="color: #25325F;">Пресет</span>
      <v-btn icon small @click="closePresetModal">
        <v-icon style="color: #9BA1B5;">mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <!-- Body -->
    <v-card-text class="px-6 pt-5 pb-2">
      <v-form ref="form" lazy-validation>

      <!-- Название -->
      <div class="mb-4">
        <label class="preset-label">Название <span style="color:#EC2E2E;">*</span></label>
        <v-text-field
          v-model="form.name"
          outlined
          dense
          :rules="[v => !!v && v.trim().length > 0 || 'Введите название']"
          autofocus
          class="preset-input mt-1"
        />
      </div>

      <!-- Описание -->
      <div class="mb-5">
        <label class="preset-label">Описание</label>
        <v-textarea
          v-model="form.description"
          outlined
          dense
          hide-details
          rows="3"
          no-resize
          class="preset-input mt-1"
        />
      </div>

      <!-- Компетенции -->
      <div>
        <label class="preset-label mb-3 d-block">
          Компетенции <span style="color:#EC2E2E;">*</span>
          <span style="color: #9BA1B5; font-weight: 500;">({{ form.competences.length }})</span>
        </label>
        <div v-if="competencesError" class="preset-error-text mb-2">Выберите хотя бы одну компетенцию</div>

        <div class="preset-competencies-list">
          <div
            v-for="item in competences"
            :key="item.id"
            class="preset-competency-item"
            :class="{ 'preset-competency-item--active': form.competences.includes(item.id) }"
            @click="toggleCompetency(item.id)"
          >
            <div class="preset-checkbox">
              <v-icon
                v-if="form.competences.includes(item.id)"
                size="16"
                color="#009E60"
              >
                mdi-check-circle
              </v-icon>
              <div v-else class="preset-checkbox__empty"></div>
            </div>
            <span class="preset-competency-title">{{ item.name }}</span>
          </div>
        </div>
      </div>
      </v-form>
    </v-card-text>

    <v-divider></v-divider>

    <!-- Footer -->
    <v-card-actions class="px-6 py-4 d-flex justify-end" style="gap: 12px;">
      <v-btn
        text
        height="40"
        class="px-5"
        style="color: #6B759A; font-weight: 600;"
        @click="closePresetModal"
      >
        Отмена
      </v-btn>
      <v-btn
        depressed
        height="40"
        class="px-8"
        color="#009E60"
        dark
        style="font-weight: 600; border-radius: 8px;"
        @click="save"
      >
        Сохранить
      </v-btn>
    </v-card-actions>

  </v-card>
</v-dialog>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

export default {
  name: 'PresetEditModal',
  model: { prop: 'value', event: 'input' },
  props: {
    value: { type: Boolean, default: false },
    preset: { type: Object, default: null },
    loading: { type: Boolean, default: true },
  },
  emits: ['save'],
  data() {
    return {
      competencesError: false,
      form: {
        name: '',
        description: '',
        competences: [],
      }
    };
  },
  computed: {
    ...mapGetters({
      competences: 'diagnostics/diagnotics/libraryCompetencies',
    }),
  },
  watch: {
    preset: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form = {
            name: val.name || '',
            description: val.description || '',
            competences: val.competences
              ? val.competences.map(c => (typeof c === 'object' ? c.id : c))
              : [],
          };
        } else {
          this.resetForm();
        }
      }
    },
    value: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.resetForm();
        }
      }
    },
  },
  methods: {
    ...mapMutations({
      SET_PRESET_SHOW_MODAL: 'diagnostics/diagnotics/SET_PRESET_SHOW_MODAL',
      SET_PRESET_ID_MODAL: 'diagnostics/diagnotics/SET_PRESET_ID_MODAL',
    }),
    toggleCompetency(id) {
      const idx = this.form.competences.indexOf(id);
      if (idx === -1) {
        this.form.competences.push(id);
      } else {
        this.form.competences.splice(idx, 1);
      }
    },
    save() {
      const formValid = this.$refs.form.validate();
      this.competencesError = this.form.competences.length === 0;
      if (!formValid || this.competencesError) return;
      this.$emit('save', JSON.parse(JSON.stringify(this.form)));
    },
    resetForm() {
      this.form = { name: '', description: '', competences: [] };
      this.competencesError = false;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    closePresetModal() {
      this.SET_PRESET_SHOW_MODAL(false);
      this.SET_PRESET_ID_MODAL(null);
    },
  }
};
</script>

<style lang="scss">
.preset-dialog {
  max-height: 90vh;
}

.preset-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #25325F;
}

.preset-input {
  .v-input__slot {
    border-radius: 8px !important;
    background-color: #ffffff !important;
    box-shadow: none !important;
    fieldset {
      border-color: #DEE1EA !important;
    }
  }
  &.v-input--is-focused .v-input__slot fieldset {
    border-color: #009E60 !important;
    border-width: 2px !important;
  }
  input, textarea {
    font-size: 14px;
    color: #25325F;
    &::placeholder { color: #9BA1B5; }
  }
}

/* ---- список компетенций ---- */
.preset-competencies-list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;

  // скроллбар
  &::-webkit-scrollbar       { width: 4px; }
  &::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #DEE1EA; border-radius: 4px;
    &:hover { background: #9BA1B5; }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

.preset-competency-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #DEE1EA;
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  user-select: none;

  &:hover {
    border-color: #009E60;
    background: #F0FBF6;
  }

  &--active {
    border-color: #009E60 !important;
    background: #F0FBF6 !important;
  }
}

.preset-checkbox {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &__empty {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #DEE1EA;
    transition: border-color 0.15s;

    .preset-competency-item:hover & {
      border-color: #009E60;
    }
  }
}

.preset-competency-title {
  font-size: 13px;
  font-weight: 500;
  color: #25325F;
  line-height: 1.3;
}

.preset-error-text {
  font-size: 12px;
  color: #EC2E2E;
}

.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loader-content {
  text-align: center;
}

.loader-text {
  color: #6B759A;
  font-size: 14px;
  font-weight: 500;
}

.blur-content {
  filter: blur(2px);
  pointer-events: none;
  user-select: none;
}
</style>
